import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { alpha, Box, styled, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import type { ReactNode } from 'react';

import { CARD_SX } from '~/theme';

const LabelTypography = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  lineHeight: 1.3,
  color: theme.palette.text.secondary,
}));

export type MetricTileProps = {
  label: string;
  value: ReactNode;
  testId: string;
  size?: { xs: number; sm?: number; md: number };
  /** Table column field this tile sorts by. Omit for info-only tiles. */
  sortField?: string;
  sortModel?: GridSortModel;
  onSort?: (field: string) => void;
  /**
   * At most one tile carries a verdict — emphasis only works if the rest stay quiet.
   * `success` is money the page won, `error` money it stands to lose; `info` passes no
   * verdict and qualifies the rest of the row, so it can sit beside one that does.
   *
   * Deliberately no `warning`: the property table renders its missed-target sign in the
   * same orange.
   */
  accent?: 'success' | 'error' | 'info';
  /** Rule separating this tile from the one before it. Off for the first. */
  divided?: boolean;
  /** Makes the whole tile a link. Ignored when `sortField` is set: a tile cannot both navigate away and re-sort the table under it. */
  href?: string;
  /** Explains what the metric measures, behind an info icon beside the label. */
  info?: string;
  /**
   * Marketing-screenshot crop target (`screenshots/shots.ts`), deliberately
   * separate from a testid: a testid gets renamed freely by whoever's writing
   * tests, and a capture target must not break silently when someone tidies a
   * selector. Left unset on every KPI tile except the one call site the
   * screenshot manifest crops.
   */
  dataShot?: string;
};

/** One figure in a {@link MetricBand}: a quiet label over the number. */
export function MetricTile({
  label,
  value,
  testId,
  size = { xs: 6, sm: 4, md: 2 },
  sortField,
  sortModel,
  onSort,
  accent,
  divided = true,
  href,
  info,
  dataShot,
}: MetricTileProps) {
  const isSortable = Boolean(sortField && onSort);
  const isLink = Boolean(href) && !isSortable;
  const isInteractive = isSortable || isLink;
  const activeSort = sortField ? sortModel?.find((s) => s.field === sortField) : undefined;
  // The accent tile skips the rule: a line tracing the corners of a washed, rounded
  // box reads as a seam rather than a separator.
  const hasRule = divided && !accent;

  const handleSort = () => {
    if (sortField && onSort) onSort(sortField);
  };

  return (
    <Grid
      data-shot={dataShot}
      size={size}
      container
      alignItems="start"
      direction="column"
      component={isLink ? 'a' : 'div'}
      href={isLink ? href : undefined}
      sx={{
        gap: 0.25,
        textDecoration: 'none',
        color: 'inherit',
        px: { xs: 1, md: 1.5 },
        py: 0.75,
        // A rule needs square corners to meet the tile's edges. Only the ruleless tiles —
        // the first one and the accent — keep the radius.
        borderRadius: hasRule ? { xs: 2, md: 0 } : 2,
        // Full height plus `mt: auto` on the value below: the labels are not all one line,
        // and without this the numbers sit at whatever height their own label left them.
        height: '100%',
        // Only from `md`, where all six sit on one row. Narrower than that they wrap, and a
        // left rule lands at the start of a row.
        ...(hasRule && {
          borderLeft: { xs: 0, md: '1px solid' },
          borderColor: { md: 'divider' },
        }),
        ...(accent && {
          bgcolor: (theme) => alpha(theme.palette[accent].main, 0.08),
        }),
        ...(isInteractive && {
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          '&:hover': {
            backgroundColor: (theme) =>
              accent ? alpha(theme.palette[accent].main, 0.16) : theme.palette.action.hover,
          },
        }),
      }}
      role={isSortable ? 'button' : undefined}
      tabIndex={isSortable ? 0 : undefined}
      aria-label={isSortable ? `Sort by ${label}` : undefined}
      onClick={isSortable ? handleSort : undefined}
      onKeyDown={
        isSortable
          ? // Annotated because the polymorphic `component` above widens the
            // Grid's prop type to a union, which drops the handler's inference.
            (e: React.KeyboardEvent<HTMLElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSort();
              }
            }
          : undefined
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <LabelTypography sx={accent ? { color: `${accent}.dark`, fontWeight: 600 } : undefined}>
          {label}
        </LabelTypography>
        {activeSort ? (
          activeSort.sort === 'desc' ? (
            <ArrowDownward sx={{ fontSize: 14, color: 'text.secondary' }} />
          ) : (
            <ArrowUpward sx={{ fontSize: 14, color: 'text.secondary' }} />
          )
        ) : null}
        {info ? (
          <Tooltip disableFocusListener disableTouchListener disableInteractive title={info}>
            <InfoOutlined
              aria-label={`About ${label}`}
              sx={{ fontSize: 14, color: 'text.disabled' }}
            />
          </Tooltip>
        ) : null}
      </Box>
      <Typography
        variant="h5"
        data-testid={testId}
        sx={{
          fontWeight: 600,
          lineHeight: 1.2,
          mt: 'auto',
          // Proportional figures: `tabular-nums` gives every digit a `0`'s width, which reads
          // loose at this size. Tabular is for columns.
          color: accent ? `${accent}.dark` : 'text.primary',
          fontSize: { xs: '1.05rem', md: '1.2rem' },
        }}
      >
        {value}
      </Typography>
    </Grid>
  );
}

/** The summary row a list page opens with: a single card of {@link MetricTile}s on the page's tinted band. */
export function MetricBand({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ backgroundColor: '#F9FAFB', paddingY: 0.5 }}>
      <Grid sx={CARD_SX} px={1} py={0.25} bgcolor="white" container>
        <Grid size={12} container spacing={{ xs: 0.5, md: 0 }}>
          {children}
        </Grid>
      </Grid>
    </Box>
  );
}
